/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'
import { injectIntl } from 'react-intl'

import { EuiFormRow, EuiSelect, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  DeploymentTemplateInfoV2,
  DeploymentTemplateRequestBody,
  ElasticsearchClusterPlan,
} from '@modules/cloud-api/v1/types'

import {
  getAutoscalingStatus,
  isAutoscalingSupportedInTemplate,
} from '@/lib/stackDeployments/selectors/autoscaling'
import {
  setAutoscalingDisabledForTemplate,
  setAutoscalingEnabled,
  setMLOnlyAutoscalingEnabled,
} from '@/lib/stackDeployments/autoscaling'
import { getBlankNodeConfigurationPerTemplate } from '@/lib/stackDeployments/templateBlanks'

import type { WrappedComponentProps } from 'react-intl'

export interface Props extends WrappedComponentProps {
  deploymentTemplate: DeploymentTemplateRequestBody
  updateDeploymentTemplate: (deploymentTemplate: DeploymentTemplateRequestBody) => void
}

class TemplateAutoscalingToggle extends Component<Props> {
  render() {
    const {
      deploymentTemplate,
      updateDeploymentTemplate,
      intl: { formatMessage },
    } = this.props

    const autoscalingAvailable = isAutoscalingSupportedInTemplate({
      deploymentTemplate: deploymentTemplate.deployment_template,
    })

    if (!autoscalingAvailable) {
      return null
    }

    const autoscalingStatus = getAutoscalingStatus({
      deployment: deploymentTemplate.deployment_template,
    })

    const options = [
      {
        value: 'all',
        text: formatMessage({
          id: 'autoscaling-options.all',
          defaultMessage: `Deployment (Data and Machine Learning)`,
        }),
      },
      {
        value: 'ml',
        text: formatMessage({
          id: 'autoscaling-options.ml',
          defaultMessage: `Machine Learning only`,
        }),
      },
      {
        value: 'none',
        text: formatMessage({
          id: 'autoscaling-options.none',
          defaultMessage: `None`,
        }),
      },
    ]
    const value = autoscalingStatus

    const label = (
      <FormattedMessage
        id='template-autoscaling-toggle.label'
        defaultMessage='Enable autoscaling for:'
      />
    )
    const helpText = (
      <EuiText color='subdued' size='s'>
        <FormattedMessage
          id='template-autoscaling-toggle.description'
          defaultMessage='Autoscaling allows you to automatically monitor and adjust capacity, and maintain steady, predictable performance.'
        />
      </EuiText>
    )

    return (
      <EuiFormRow label={label} helpText={helpText}>
        <EuiSelect
          disabled={!updateDeploymentTemplate}
          id='template-autoscaling-enable'
          data-test-id='template-autoscaling-enable'
          options={options}
          value={value}
          onChange={this.onChange}
        />
      </EuiFormRow>
    )
  }

  onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { deploymentTemplate, updateDeploymentTemplate } = this.props
    const { deployment_template: deployment } = deploymentTemplate

    let newDeploymentCreateRequest

    const blankTemplate = getBlankNodeConfigurationPerTemplate({
      sliderInstanceType: 'elasticsearch',
      deploymentTemplate: deploymentTemplate! as DeploymentTemplateInfoV2,
    }) as ElasticsearchClusterPlan

    if (e.target.value === 'ml') {
      // this is the special case where we just want to add `autoscaling_tier_override` to the ML tier, but leave all other autoscaling options off
      newDeploymentCreateRequest = setMLOnlyAutoscalingEnabled({
        deployment,
        blankTemplate,
        templateOnly: true,
      })
    } else if (e.target.value === 'none') {
      newDeploymentCreateRequest = setAutoscalingDisabledForTemplate({ deployment })
    } else {
      newDeploymentCreateRequest = setAutoscalingEnabled({
        deployment,
        blankTemplate,
      })
    }

    updateDeploymentTemplate({
      ...deploymentTemplate,
      deployment_template: newDeploymentCreateRequest,
    })
  }
}

export default injectIntl(TemplateAutoscalingToggle)
